import { Close, Drag, Radio } from '@fountain/fountain-ui-components';
import {
  Button,
  Drawer,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { ApprovalsService, CancelablePromise } from 'api-clients/monolith';
import clsx from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { FormattedMessage, useIntl } from 'react-intl';

import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import { ConditionBuilder } from './ConditionBuilder';
import { CatalogState } from './conditionForm';
import messages from './messages';
import {
  draftFor,
  draftForNew,
  emptyLevel,
  hasErrors,
  hiddenScopeConditions,
  levelHasError,
  LevelDraft,
  reorderLevels,
  RuleDraft,
  RulePayload,
  SCOPE_CONDITION_PREFIX,
  scopeDropsAlways,
  toPayload,
  validate,
  withScopeChoice,
} from './ruleForm';
import { useStyles } from './styles';
import { ApprovalRule, OFFER, SubjectType } from './types';

export interface UserOption {
  external_id: string;
  name: string;
}

/** What a successful create returns; update returns the same rule shape. */
type SaveRuleResponse = Awaited<
  ReturnType<typeof ApprovalsService.postInternalApiApprovalsRules>
>;

export interface RuleEditorFormProps {
  /** The rule being edited; absent means this is a new rule. */
  rule?: ApprovalRule;
  /** Only used when creating — an existing rule's type is fixed. */
  subjectType?: SubjectType;
  userOptions: UserOption[];
  /**
   * The typed field catalog the condition builder is driven by (AXHE-3929),
   * carried as a state rather than an array so the builder can tell a failed
   * fetch from an account with nothing to gate on.
   */
  catalog: CatalogState;
  /** Retries the catalog fetch, offered when it failed. */
  onRetryCatalog: () => void;
  onClose: () => void;
  onSaved: () => void;
}

export interface RuleEditorPanelProps extends RuleEditorFormProps {
  open: boolean;
}

/**
 * The scope radio group's anchors.
 *
 * Module constants rather than derived per render: a `<fieldset>` takes its
 * accessible name from its `<legend>`, and the group's description is whichever
 * of the notes under it is showing — both referenced by id, and the panel is a
 * singleton so the ids cannot collide with a second instance. The
 * `-helper-text` suffix is the shape MUI derives for a `TextField`, so one
 * convention covers every control in the panel.
 */
const SCOPE_LEGEND_ID = 'approval-rule-scope-label';
const SCOPE_HELPER_ID = 'approval-rule-scope-helper-text';
const SCOPE_HIDDEN_ID = 'approval-rule-scope-hidden-conditions';
const SCOPE_ALWAYS_DROP_ID = 'approval-rule-scope-always-dropped';

/**
 * Create and edit an approval rule.
 *
 * A right-hand slide-in rather than a centred modal: this is significant data
 * entry — a name and a variable number of levels each with their own approvers —
 * and a modal is the wrong container for that. It also lets the list stay
 * visible behind the panel, which is what makes "another rule already covers
 * this" noticeable while you are writing one.
 *
 * ## What this panel deliberately cannot switch on or off
 *
 * Neither the rule's `enabled` nor a level's (AXHE-4470). Design Requirements §2
 * locks "only whole rules have an enable/disable toggle. Individual levels
 * cannot be toggled on/off", and the mock puts the rule's one toggle in the
 * list row's Enabled column — not in here. Two switches shipped anyway during
 * the original transcription (AXHE-3945): a per-level "Level active" and a
 * second "Rule active" duplicating the row's.
 *
 * Both values are still *carried* — see `toPayload`. Dropping them would not be
 * a UI-only change: `SaveRule#create_level` defaults an absent `enabled` to
 * true, so a payload that stopped naming it would silently re-activate a level
 * an admin had deliberately switched off, and `RuleMatcher` would start routing
 * approvals through an approver they meant to bypass. So an already-off level
 * keeps its state and says so instead, since nothing can switch it back.
 *
 * ## What it edits, and what it only carries
 *
 * Data-key conditions are edited here via `ConditionBuilder` (AXHE-3929) — both
 * a level's and, since AXHE-4467, the rule's own scope, which the "Apply this
 * rule to" choice below sits on. Every other condition type is carried back
 * untouched; see `toPayload`, where getting this wrong would clear them.
 *
 * That split is why the "All …" choice can be withheld. Choosing it means "drop
 * every condition scoping this rule", and a panel that cannot render a condition
 * must not offer to delete it — see `hiddenScopeConditions`.
 */
export const RuleEditorForm: React.FC<RuleEditorFormProps> = ({
  rule,
  subjectType = OFFER,
  userOptions,
  catalog,
  onRetryCatalog,
  onClose,
  onSaved,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const [draft, setDraft] = useState<RuleDraft>(() =>
    rule ? draftFor(rule) : draftForNew(subjectType),
  );
  // Validation runs on submit, not on keystroke — flagging a name as missing
  // while the user is still walking toward the field is premature.
  const [submitted, setSubmitted] = useState(false);

  // Undefined until the catalog resolves. Both `validate` and `toPayload` treat
  // that as "conditions were not editable this session", which is exactly true:
  // the builder renders its own loading and error states instead of a picker.
  const fieldCatalog = catalog.status === 'ready' ? catalog.catalog : undefined;

  /**
   * Mints the key for each level added here.
   *
   * A counter that only ever goes up, not `levels.length`: length is not
   * monotonic, so add → remove → add re-issued a key that was already on a card
   * (add to a one-level draft gives `level-new-1`, removing the other card puts
   * the length back to 1, and the next add gives `level-new-1` again).
   *
   * That matters more than a duplicate React key. Every mutation in this panel
   * routes on `level.key` — `updateLevel` patches *every* level whose key
   * matches and remove filters *every* level whose key matches — so two cards
   * sharing a key stop behaving as separate levels while `toPayload` still
   * submits both, and a rule can be saved whose second level holds approvers
   * the admin only entered once.
   *
   * A ref rather than state: it must survive a render without causing one, and
   * nothing renders from it. The `level-new-` prefix keeps these clear of the
   * `level-N` keys `draftFor` mints for persisted levels, so the counter only
   * has to be unique against itself and can start at zero.
   */
  const nextLevelKey = useRef(0);
  const mintLevelKey = () => {
    const key = `level-new-${nextLevelKey.current}`;
    nextLevelKey.current += 1;
    return key;
  };

  const errors = validate(draft, fieldCatalog);
  const showErrors = submitted && hasErrors(errors);

  // Both the service and the options must keep a stable identity across renders.
  // Passed inline they are new objects every time, and the hook's internals
  // re-derive from them — which turns each keystroke in the name field into
  // another render, and the form tears itself down once React gives up. The
  // sibling `useApiService` documents the same requirement in as many words.
  // Annotated rather than inferred: create and update declare their responses
  // separately in the OpenAPI document, so the ternary infers a union of two
  // structurally identical types and the mutation hook — which resolves a single
  // `T` — cannot match it. The panel only ever reads `result.status`, so the
  // narrower of the two is the honest common type.
  const saveRule = useCallback(
    (payload: RulePayload): CancelablePromise<SaveRuleResponse> =>
      rule
        ? ApprovalsService.patchInternalApiApprovalsRules(rule.id as string, payload)
        : ApprovalsService.postInternalApiApprovalsRules(payload),
    [rule],
  );
  const mutationOptions = useMemo(() => ({ onSuccess: onSaved }), [onSaved]);

  // Type arguments given rather than inferred: the hook's `T` only appears in the
  // service's return and in `onSuccess`, and this `onSuccess` takes no argument —
  // so there is nothing for TypeScript to infer `T` from and it lands on
  // `unknown`, which the annotated service then fails to satisfy.
  const { result, mutation } = useApiServiceMutation<SaveRuleResponse, typeof saveRule>(
    saveRule,
    mutationOptions,
  );

  const updateLevel = (key: string, patch: Partial<LevelDraft>) =>
    setDraft(current => ({
      ...current,
      levels: current.levels.map(level =>
        level.key === key ? { ...level, ...patch } : level,
      ),
    }));

  /**
   * Moves a level to where it was dropped.
   *
   * Level order is the approval sequence — level 1 signs off before level 2 gets
   * asked — so this is an edit to the rule, not a rearrangement of a view of it.
   * See `reorderLevels` for why moving the array entry is the entire change.
   */
  const handleDragEnd = ({ source, destination }: DropResult) => {
    // No destination means the level was dropped outside the list, which reads
    // as a cancel rather than as a move to the end.
    if (!destination || destination.index === source.index) return;

    setDraft(current => ({
      ...current,
      levels: reorderLevels(current.levels, source.index, destination.index),
    }));
  };

  const handleSave = () => {
    setSubmitted(true);
    if (hasErrors(validate(draft, fieldCatalog))) return;

    mutation(toPayload(draft, fieldCatalog));
  };

  /**
   * What the entity-naming copy selects on.
   *
   * A rule's subject type is fixed at creation, so this is constant for the life
   * of the panel — but it is read off the draft rather than the `subjectType`
   * prop, which is only passed when creating.
   */
  const subjectValues = { subject: draft.subjectType };

  // Editing names the entity now too (AXHE-4467): one rule table backs both
  // filters, so "Edit rule" left the panel unable to say which of the two an
  // admin had opened.
  const title = rule ? messages.editRuleTitle : messages.createRuleTitle;

  // The scope rows the panel is not showing. Non-empty means the rule is scoped
  // by something it cannot render, which is what withholds "All …".
  const hiddenScope = hiddenScopeConditions(draft, fieldCatalog);
  // Why they are hidden decides which note explains it. With no catalog the
  // hidden rows are the rule's *own* data-key conditions, kept off screen only
  // because the builder is showing its error state instead of them — recoverable
  // by the Retry it offers. With a catalog they are condition types this panel
  // never renders (position, location, opening, attribute — AXHE-4383), which is
  // permanent. Blaming the kind in the first case would name a cause the admin
  // can see is false one element down.
  const hiddenScopeNote = fieldCatalog
    ? messages.scopeConditionsElsewhere
    : messages.scopeConditionsUnavailable;
  const scopeIsAll = draft.scopeChoice === 'all';
  // Only worth saying with a sequence to describe: a one-level rule has no next
  // level to move to.
  const showScopeAllNote = scopeIsAll && draft.levels.length > 1;
  const scopeNeedsCondition = showErrors && errors.scope === 'no_conditions';
  // A stored "matches everything" row sitting beside a narrowing one, on the
  // conditional choice: saving drops it. Under OR that row is what opens the
  // gate today, so the drop narrows the rule and the note says so; under AND it
  // is redundant and the note only reports the tidy-up. Named off the logic
  // rather than the panel's choice, which is already `conditions` here.
  const showScopeAlwaysDrop = scopeDropsAlways(draft, fieldCatalog);
  const scopeAlwaysDropNote =
    draft.scopeConditionLogic === 'or'
      ? messages.scopeAlwaysDroppedNarrows
      : messages.scopeAlwaysDropped;
  const scopeDescribedBy =
    [
      showScopeAllNote || scopeNeedsCondition ? SCOPE_HELPER_ID : undefined,
      hiddenScope.length > 0 ? SCOPE_HIDDEN_ID : undefined,
      showScopeAlwaysDrop ? SCOPE_ALWAYS_DROP_ID : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <>
      <div
        className={styles.panel}
        role="dialog"
        aria-label={intl.formatMessage(title, subjectValues)}
      >
        {/* h3, not h5: this theme's h5 is 12px, which would leave the panel's
            own title smaller than every line of copy beneath it. h3 is what the
            other recruiter-ui panels and modals use for the same job. */}
        <Typography variant="h3">
          <FormattedMessage {...title} values={subjectValues} />
        </Typography>

        {/* Edit only. The mock gives create no intro, and there is nothing to
            introduce: an empty panel's own fields say what it collects, while an
            existing rule's do not say what an edit here will and will not
            touch. */}
        {rule && (
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage {...messages.editRuleIntro} values={subjectValues} />
          </Typography>
        )}

        {result.status === 'error' && (
          <div className={styles.error} role="alert">
            <Typography variant="body1">
              <FormattedMessage {...messages.saveFailed} />
            </Typography>
          </div>
        )}

        {/* `required` rather than an asterisk written into the label: it marks
            the label *and* puts `required` on the input, so the field announces
            itself as mandatory to a screen reader before the save is refused
            rather than after. There is no <form> around this panel, so it
            brings no native validation bubble to compete with the message
            below the field.

            Design Requirements §3 asks for a *red* asterisk (AXHE-4468); this
            one inherits the label's colour and only goes red once the field is
            actually in error, which is MUI's own behaviour and is left alone
            deliberately. `constraint-anti-pattern-guard`'s semantic colour
            table reserves error red for "failures, validation errors,
            destructive actions" — a field being mandatory is none of those,
            and spending the error colour on the resting state is what makes it
            stop reading as an error when the field really is one. */}
        <TextField
          id="approval-rule-name"
          className={styles.field}
          label={intl.formatMessage(messages.ruleNameLabel)}
          required
          value={draft.name}
          // The value is read out of the event *before* the updater, not inside
          // it, and that is load-bearing: reading it inside took the whole
          // panel down on the third keystroke.
          //
          // React 16 pools synthetic events, and only computes a lazy `useState`
          // updater eagerly — while the event is still alive — when no other
          // work is pending on the fiber. By the third character there always
          // is, so the updater ran at render time instead, against an event
          // React had already released, and threw on `event.target`. With no
          // error boundary above it React 16 unmounts the entire root, so the
          // panel does not misbehave, it disappears. Typing a rule name is the
          // first thing anyone does in here.
          //
          // Every other handler in this file already reads `event.target`
          // eagerly, as a call argument; this was the one lazy read.
          onChange={event => {
            const { value } = event.target;
            setDraft(current => ({ ...current, name: value }));
          }}
          error={showErrors && errors.name === 'required'}
          helperText={
            showErrors && errors.name === 'required'
              ? intl.formatMessage(messages.ruleNameRequired)
              : undefined
          }
          fullWidth
        />

        {/* Directly under the name, as the mock has it, and before the levels
            for a reason: this is the question of which offers the rule touches
            at all, and the levels below only describe who signs off once it
            does. Reading them in the other order asks the admin to hold a
            sequence of approvers in mind before knowing what triggers it. */}
        {/* Named, because the panel renders one condition builder per level as
            well as this one: "Add condition" and a condition row are ambiguous
            across the whole panel, so a test that means the rule's scope has to
            say so. */}
        <div className={styles.scopeSection} data-testid="rule-scope">
          <FormControl component="fieldset" fullWidth>
            {/* A legend, because a radio group has no single control to hang a
                label on. Sized to h4 (14px medium) rather than left at MUI's
                default: it is a section label under an 18px title, and the
                theme's own `subtitle2` is 18px, which would make it compete.

                It carries an id and the group points at it. The fieldset/legend
                pair is *not* enough on its own: MUI renders `RadioGroup` as a
                `div role="radiogroup"` inside the fieldset, and it is that div
                the assistive layer treats as the group — the legend names the
                fieldset around it, so without the reference below the group has
                no accessible name at all. */}
            <FormLabel
              component="legend"
              className={styles.scopeLegend}
              id={SCOPE_LEGEND_ID}
            >
              <FormattedMessage {...messages.scopeLegend} />
            </FormLabel>

            <RadioGroup
              // `name` is what scopes native radio traversal, so the pair is one
              // tab stop and the arrow keys move between the choices. MUI
              // generates one when it is omitted; naming it keeps it stable
              // across mounts and legible in a DOM dump.
              name="approval-rule-scope"
              value={draft.scopeChoice}
              aria-labelledby={SCOPE_LEGEND_ID}
              // Described on the group rather than on each radio, so the note
              // under it is announced once on entry instead of again on every
              // arrow press.
              aria-describedby={scopeDescribedBy}
              // Marks the group invalid to assistive tech once the save is
              // refused for it, the counterpart of the `error` prop MUI puts on
              // the name TextField. Without it a screen-reader user re-entering
              // the group after a refusal hears `scopeNeedsCondition` as a
              // neutral description rather than as the error it is — the one
              // gate-relevant validation message on the panel would be its least
              // discoverable.
              aria-invalid={scopeNeedsCondition || undefined}
              onChange={event =>
                setDraft(current =>
                  withScopeChoice(
                    current,
                    event.target.value as RuleDraft['scopeChoice'],
                  ),
                )
              }
            >
              {/* Neither carries `checked`: the group's `value` decides which is
                  selected, and MUI's Radio derives it from `RadioGroupContext`
                  when the prop is absent. Passing both left one piece of state
                  with two owners that happened to agree.

                  Withheld, not hidden: the "All …" choice exists and is
                  unavailable for a reason the note below states. Hiding it would
                  leave a radio group of one, which reads as a control that does
                  nothing. */}
              <Radio
                value="all"
                disabled={hiddenScope.length > 0}
                label={intl.formatMessage(messages.scopeAll, subjectValues)}
              />
              <Radio
                value="conditions"
                label={intl.formatMessage(
                  messages.scopeConditional,
                  subjectValues,
                )}
              />
            </RadioGroup>

            {/* The first two share one id because they can never both be due:
                the validation message belongs to the conditional choice and the
                pass-through explanation to the other. The note below them is a
                property of the rule rather than of the choice, so it has an id
                of its own and both can be referenced at once. */}
            {scopeNeedsCondition && (
              // `role="alert"` because it is mounted only when the save is
              // refused, so its appearance is the event to announce — the group
              // itself is only re-read on focus, which a refused save does not
              // move.
              <FormHelperText id={SCOPE_HELPER_ID} error role="alert">
                <FormattedMessage
                  {...messages.scopeNeedsCondition}
                  values={subjectValues}
                />
              </FormHelperText>
            )}
            {showScopeAllNote && (
              <FormHelperText id={SCOPE_HELPER_ID}>
                <FormattedMessage
                  {...messages.scopeAllLevels}
                  values={subjectValues}
                />
              </FormHelperText>
            )}
            {hiddenScope.length > 0 && (
              <FormHelperText id={SCOPE_HIDDEN_ID}>
                <FormattedMessage {...hiddenScopeNote} values={subjectValues} />
              </FormHelperText>
            )}
            {/* The "matches everything" row that a save will drop. `role="status"`
                rather than `alert`: it is not a validation failure and does not
                block the save, it is a heads-up about a change the save makes to
                the stored data. It is mounted only while that data is present, so
                its appearance is worth announcing without stealing focus. */}
            {showScopeAlwaysDrop && (
              <FormHelperText id={SCOPE_ALWAYS_DROP_ID} role="status">
                <FormattedMessage
                  {...scopeAlwaysDropNote}
                  values={subjectValues}
                />
              </FormHelperText>
            )}
          </FormControl>

          {/* Outside the FormControl above, deliberately. MUI's FormControl
              pushes `error`, `disabled` and `margin` down to every input inside
              it through context, which would let the scope group's own state
              overwrite what each condition row is saying about itself. */}
          {!scopeIsAll && (
            <ConditionBuilder
              conditions={draft.scopeConditions}
              catalog={catalog}
              onRetryCatalog={onRetryCatalog}
              showErrors={showErrors}
              idPrefix={SCOPE_CONDITION_PREFIX}
              showHeading={false}
              onChange={scopeConditions =>
                setDraft(current => ({ ...current, scopeConditions }))
              }
              logic={draft.scopeConditionLogic}
              onLogicChange={scopeConditionLogic =>
                setDraft(current => ({ ...current, scopeConditionLogic }))
              }
            />
          )}
        </div>

        {/* A level's number is its place in the sequence, so the heading is
            derived from the array index rather than stored — which is what makes
            a reorder renumber the cards it moved without touching the drafts. */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="approval-rule-levels">
            {(droppable, listSnapshot) => (
              <div
                ref={droppable.innerRef}
                {...droppable.droppableProps}
                className={clsx(
                  styles.levelList,
                  listSnapshot.isDraggingOver && styles.levelListDropTarget,
                )}
              >
                {draft.levels.map((level, index) => (
                  <Draggable
                    key={level.key}
                    draggableId={level.key}
                    index={index}
                    // A one-level rule has nothing to reorder. This is also what
                    // withholds the handle: a disabled draggable is handed a null
                    // `dragHandleProps`, so the control exists exactly when
                    // using it would do something.
                    isDragDisabled={draft.levels.length === 1}
                  >
                    {(draggable, snapshot) => (
                      <div
                        ref={draggable.innerRef}
                        {...draggable.draggableProps}
                        className={clsx(
                          styles.levelCard,
                          snapshot.isDragging && styles.levelCardDragging,
                        )}
                        data-testid="level-card"
                      >
                        <div className={styles.levelCardHeader}>
                          <div className={styles.levelCardTitle}>
                            {draggable.dragHandleProps && (
                              <span
                                {...draggable.dragHandleProps}
                                className={styles.dragHandle}
                                aria-label={intl.formatMessage(
                                  messages.reorderLevel,
                                  { number: index + 1 },
                                )}
                              >
                                <Drag fontSize="small" />
                              </span>
                            )}

                            <Typography variant="body2">
                              <FormattedMessage
                                {...messages.levelHeading}
                                values={{ number: index + 1 }}
                              />
                            </Typography>
                          </div>

                          {/* Destructive, so it is never primary weight — a text
                              button at the end of the header row, not a filled
                              button next to Save.

                              An icon, not a `×` text node, for the reason the
                              condition builder's remove already gives: a glyph
                              has no icon box, so the button measured 17px square
                              — under the minimum pointer target. */}
                          {draft.levels.length > 1 && (
                            <IconButton
                              size="small"
                              aria-label={intl.formatMessage(messages.removeLevel)}
                              onClick={() =>
                                setDraft(current => ({
                                  ...current,
                                  levels: current.levels.filter(
                                    l => l.key !== level.key,
                                  ),
                                }))
                              }
                            >
                              <Close className={styles.removeIcon} />
                            </IconButton>
                          )}
                        </div>

                        <TextField
                          id={`level-name-${level.key}`}
                          className={styles.field}
                          label={intl.formatMessage(messages.levelNameLabel)}
                          value={level.name}
                          onChange={event =>
                            updateLevel(level.key, { name: event.target.value })
                          }
                          fullWidth
                        />

                        <TextField
                          id={`level-approvers-${level.key}`}
                          className={styles.field}
                          label={intl.formatMessage(messages.approversLabel)}
                          value={level.approverIds}
                          onChange={event =>
                            updateLevel(level.key, {
                              approverIds: event.target.value as unknown as string[],
                            })
                          }
                          error={showErrors && levelHasError(errors, level.key, 'no_approvers')}
                          helperText={
                            showErrors && levelHasError(errors, level.key, 'no_approvers')
                              ? intl.formatMessage(messages.approverRequired)
                              : undefined
                          }
                          SelectProps={{ multiple: true }}
                          select
                          fullWidth
                        >
                          {userOptions.map(user => (
                            <MenuItem key={user.external_id} value={user.external_id}>
                              {user.name}
                            </MenuItem>
                          ))}
                        </TextField>

                        {/*
                          The level lists approvers whose users have been
                          offboarded. They cannot be sent back (the write
                          contract needs a user_id the read API no longer has),
                          so saving drops them — which matches the engine, where
                          an offboarded approver already resolves to nobody.
                          Saying so is the point: the same edit used to shrink
                          the level in silence.
                        */}
                        {level.offboardedApproverCount > 0 && (
                          <Typography
                            variant="body2"
                            className={styles.inlineWarning}
                            role="status"
                          >
                            <FormattedMessage
                              {...messages.offboardedApprovers}
                              values={{ count: level.offboardedApproverCount }}
                            />
                          </Typography>
                        )}

                        <ConditionBuilder
                          conditions={level.conditions}
                          catalog={catalog}
                          onRetryCatalog={onRetryCatalog}
                          showErrors={showErrors}
                          idPrefix={level.key}
                          onChange={conditions =>
                            updateLevel(level.key, { conditions })
                          }
                          logic={level.conditionLogic}
                          onLogicChange={conditionLogic =>
                            updateLevel(level.key, { conditionLogic })
                          }
                        />

                        {/*
                          Only ever reached by a level switched off before
                          AXHE-4470 removed the switch. Said out loud rather than
                          left as an invisible property: the level lists
                          approvers, sits in the sequence, and is skipped anyway,
                          so an admin reading this card has no other way to tell
                          why the rule never stops there. Read-only — this panel
                          no longer owns the value, it only declines to
                          overwrite it.
                        */}
                        {!level.enabled && (
                          <Typography
                            variant="body2"
                            className={styles.inlineWarning}
                            role="status"
                          >
                            <FormattedMessage {...messages.levelOff} />
                          </Typography>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}

                {droppable.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Button
          onClick={() => {
            // Minted outside the updater: bumping the ref inside it would make
            // the updater impure, and React may call it more than once.
            const key = mintLevelKey();
            setDraft(current => ({
              ...current,
              levels: [...current.levels, emptyLevel(key)],
            }));
          }}
        >
          <FormattedMessage {...messages.addLevel} />
        </Button>

        {/* Said plainly rather than left to be discovered: only data-key
            conditions are edited above (AXHE-3929). A level's other condition
            types are not shown here, and an admin who cannot see them has no way
            to know they survive the save. The rule's own scope says the same
            thing where it is — see `scopeConditionsElsewhere`. */}
        <Typography variant="body2" color="textSecondary" className={styles.panelNote}>
          <FormattedMessage {...messages.conditionsPreserved} />
        </Typography>

        <div className={styles.panelActions}>
          <Button onClick={onClose}>
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          {/* Two labels, not one "Save rule": creating a rule and saving an edit
              are different actions, and this is the last thing read before
              either commits. */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={result.status === 'loading'}
          >
            <FormattedMessage
              {...(rule ? messages.saveRuleChanges : messages.createRuleSubmit)}
            />
          </Button>
        </div>
      </div>
    </>
  );
};

/**
 * The form in its slide-in container.
 *
 * Split from `RuleEditorForm` so the form can be exercised on its own. MUI v4's
 * Drawer renders through a Modal portal, and under jsdom repeated re-renders of
 * the portal's contents detach it from the document — so a test that types more
 * than a character or two into a field loses the whole subtree and every
 * subsequent query fails, for reasons that have nothing to do with the form.
 *
 * The split is worth having regardless: the form owns the draft, the validation
 * and the write, and the drawer is only where it sits.
 */
export const RuleEditorPanel: React.FC<RuleEditorPanelProps> = ({
  open,
  onClose,
  ...formProps
}) => (
  <Drawer anchor="right" open={open} onClose={onClose}>
    <RuleEditorForm onClose={onClose} {...formProps} />
  </Drawer>
);
